import { useCallback } from 'react';
import { ResearchItemDetailsData } from '../use-research-item-details';

// In-memory cache for research details
const researchDetailsCache = new Map<string, {
  data: ResearchItemDetailsData;
  timestamp: number;
  expiresAt: number;
}>();

// Cache configuration
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
const MAX_CACHE_SIZE = 100; // Maximum number of cached items

export const useResearchDetailsCache = () => {
  
  const getCachedDetails = useCallback((id: string): ResearchItemDetailsData | null => {
    const cached = researchDetailsCache.get(id);
    
    if (!cached) return null;
    
    // Check if cache entry has expired
    if (Date.now() > cached.expiresAt) {
      researchDetailsCache.delete(id);
      return null;
    }
    
    return cached.data;
  }, []);
  
  const setCachedDetails = useCallback((id: string, data: ResearchItemDetailsData) => {
    // Implement LRU cache behavior
    if (researchDetailsCache.size >= MAX_CACHE_SIZE) {
      // Remove oldest entry
      const oldestKey = researchDetailsCache.keys().next().value;
      if (oldestKey) {
        researchDetailsCache.delete(oldestKey);
      }
    }
    
    const now = Date.now();
    researchDetailsCache.set(id, {
      data,
      timestamp: now,
      expiresAt: now + CACHE_DURATION
    });
  }, []);
  
  const invalidateCache = useCallback((id?: string) => {
    if (id) {
      researchDetailsCache.delete(id);
    } else {
      researchDetailsCache.clear();
    }
  }, []);
  
  const getCacheStats = useCallback(() => {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;
    
    for (const [, entry] of researchDetailsCache) {
      if (now > entry.expiresAt) {
        expiredEntries++;
      } else {
        validEntries++;
      }
    }
    
    return {
      totalEntries: researchDetailsCache.size,
      validEntries,
      expiredEntries,
      maxSize: MAX_CACHE_SIZE
    };
  }, []);
  
  const cleanExpiredEntries = useCallback(() => {
    const now = Date.now();
    const expiredKeys: string[] = [];
    
    for (const [key, entry] of researchDetailsCache) {
      if (now > entry.expiresAt) {
        expiredKeys.push(key);
      }
    }
    
    expiredKeys.forEach(key => researchDetailsCache.delete(key));
    
    return expiredKeys.length;
  }, []);
  
  return {
    getCachedDetails,
    setCachedDetails,
    invalidateCache,
    getCacheStats,
    cleanExpiredEntries
  };
}; 